// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_CORE_URL;
const API_ENDPOINT = '/api/User/user';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_CORE_SCOPE;

const HEADERS = { 'Access-Control-Allow-Origin': '*' };

// ============= Types =============

export interface UserResponse {
  id: string;
  name?: string;
  jobTitle?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  department?: string;
  companyName?: string;
  country?: string;
  office?: string;
  proNoun?: string;
  title?: string;
  isAdmin?: boolean;
}

export interface GetUserProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  userIds?: string[];
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string, includeJsonContentType = false) => ({
  Authorization: `Bearer ${token}`,
  ...(includeJsonContentType ? { 'Content-Type': 'application/json' } : {}),
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Fetches user information by user ID
 * @param getAccessToken Function to acquire access token
 * @returns Promise with API response containing user data or error
 */
export const getUser = async ({
  getAccessToken,
  userIds,
}: GetUserProps): Promise<UserResponse | UserResponse[]> => {
  try {
    console.log('🔄 Fetching user...');

    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    const url = `${BASE_URL}${API_ENDPOINT}`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    const normalizedUserIds = Array.isArray(userIds)
      ? userIds.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
      : [];
    const shouldRequestMultipleUsers = normalizedUserIds.length > 0;

    // API request
    const response = await fetch(convertURI, {
      method: shouldRequestMultipleUsers ? 'POST' : 'GET',
      headers: createHeaders(token, shouldRequestMultipleUsers),
      body: shouldRequestMultipleUsers ? JSON.stringify(normalizedUserIds) : undefined,
    });

    // Response handling
    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ User fetched successfully: ${data}`);

    return data;
  } catch (error) {
    console.error('🔴 Error fetching user:', error);
    throw error;
  }
};
